import PropTypes from 'prop-types';
import React from 'react';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import classnames from 'classnames';

import { translate as t } from '../../lib/I18n';
import { TransitModes } from '../../constants/AppConstants';
import OrderFare from '../../records/order/Fare';

import Icon from '../general/Icon';
import DateTime from '../general/DateTime';
import Duration from '../general/Duration';
import FeatureFlag from '../general/FeatureFlag';
import PureRenderComponent from '../PureRenderComponent';

import PassengerAssistStore from '../../stores/PassengerAssistStore';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import connectToStores from '../../helpers/connectToStores';
import { AccessibilityCircleIcon } from '../icons/AccessibilityCircleIcon';
import { SeatIconAvailable, SeatIconUnavailable } from '../grid/basket/v2/SeatAvailabilityStatus';


function _seatDescription (seat) {
    return t('booking.seating.coachSeat', {
        coach: seat.getCoach(),
        seat: seat.getSeat()
    });
}

function _attributesList (seat) {
    const joined = seat.getAttributeNames().join(', ');
    return { attributes: joined.charAt(0).toUpperCase() + joined.slice(1).toLowerCase() };
}



class FareJourneys extends PureRenderComponent {

    static propTypes = {
        orderFare: PropTypes.instanceOf(OrderFare).isRequired,
        isAmended: PropTypes.bool,
        showSeatingPreferences: PropTypes.bool,
        showPurchaseFlowReservations: PropTypes.bool,
    };

    static defaultProps = {
        isAmended: false,
        showSeatingPreferences: false,
        showPurchaseFlowReservations: false,
    };

    _renderLeg (leg, key) {
        const { isAmended, showSeatingPreferences, showPurchaseFlowReservations } = this.props;
        const toc = leg.toc;
        let operatorClass = '';
        if (toc && toc.name) { operatorClass = 'leg--' + toc.name.replace(/\s+/g, '-').toLowerCase(); }
        const modeClass = 'row padding-bottom leg--' + leg.mode.toLowerCase();
        const modeIconClass = 'leg__mode--' + leg.mode.replace(/\s+/g, '-').toLowerCase();
        const busReplacement = leg.mode === TransitModes.REPLACEMENT_BUS ? t('booking.journey.leg.replacementBus') : null;
        const allSeats = leg.getSeats();
        const hasAnyCountedPlace = leg.hasCountedPlaceSeats();
        const showSeatsSection = !isAmended && leg.mode === TransitModes.TRAIN;
        const sortedSeats = allSeats
            .sortBy(seat => seat.coach)
            .groupBy(seat => seat.coach)
            .toOrderedSet()
            .flatMap(seats => seats.sortBy(seat => seat.seat));

        return (
            <li key={ key } className={ `${modeClass} ${operatorClass}` }>
                <ol className="leg__stations list-unstyled col-md-6">
                    <li className="leg__station text-weight--normal">
                        { leg.departure && leg.arrival ?
                            <DateTime
                                dateTime={ leg.departure }
                                format="HH:mm"
                                className="pull-left leg__station__time text-secondary text-weight--normal"
                            />
                        : '' }

                        <div className="leg__station__name">
                            <span className="text-weight--normal">{ leg.origin.name }</span>
                        </div>
                    </li>

                    <li className="leg__operator text-weight--normal">
                        <i className={ "leg__mode " + modeIconClass } />
                        <div className="leg__operator-name">{ leg.toc ? leg.toc.name : leg.mode }</div>
                        { busReplacement && <div className="leg__replacement-text">{ busReplacement }</div> }
                    </li>

                    <li className="leg__station text-weight--normal">
                        { leg.departure && leg.arrival ?
                            <DateTime
                                dateTime={ leg.arrival }
                                format="HH:mm"
                                className="pull-left leg__station__time text-secondary text-weight--normal"
                            />
                        : '' }

                        <div className="leg__station__name">
                            <span className="text-weight--normal">{ leg.destination.name }</span>
                        </div>
                    </li>
                </ol>

                <Col md={ 6 }>
                    <Row>
                        { (showSeatingPreferences || showPurchaseFlowReservations) && showSeatsSection &&
                            <Col md={ 12 } className="seats margin-bottom--md">
                                <p className="text-primary text-weight--normal margin-bottom--sm">
                                    <span className="seat-availability-status__icon">
                                        {(hasAnyCountedPlace || allSeats.size === 0)
                                            ? <SeatIconUnavailable />
                                            : <SeatIconAvailable />}
                                    </span>
                                    { t('order.summary.seats') }
                                </p>
                                { hasAnyCountedPlace || allSeats.size === 0 ? (
                                    <p className="text-sm text-weight--normal">{ t('booking.seating.countedPlace') }</p>
                                ) : (
                                    <table className="seating-table">
                                        <tbody>
                                        { sortedSeats.map((seat, index) => (
                                            <tr key={ index } className="text-sm">
                                                <td className="fix-width text-weight--normal">{ _seatDescription(seat) }</td>
                                                <td className="text-light">{ _attributesList(seat).attributes }</td>
                                            </tr>
                                        )) }
                                        </tbody>
                                    </table>
                                ) }
                            </Col>
                        }

                        <FeatureFlag name="enablePurchaseFlowReservations">
                            { leg.bikeSeats && leg.bikeSeats.size > 0 &&
                                <Col md={ 12 } className="bike-seats">
                                    <p className="text-primary text-weight--normal margin-bottom--sm"><Icon icon="bike" />{ t('order.summary.bikes') }</p>
                                    <ul className="list-unstyled leg__bike-reservations">
                                        { leg.bikeSeats.map((bike) => {
                                            return (
                                                <li className="text-sm text-weight--normal">
                                                    { `Coach ${bike.coach}, seat ${bike.seat}` }
                                                </li>
                                            );
                                        }) }
                                    </ul>
                                </Col>
                            }
                        </FeatureFlag>
                    </Row>
                </Col>

                <span className="leg__change-trains text-sm text-weight--normal text-primary">{ t('order.summary.change_trains') }</span>
           </li>
        );
    }

    _renderOpenJourney (directionText, journey, directionIcon) {
        return (
            <Col md={ 12 } lg={ 12 } className="margin-bottom">
                <div className="border-thick--bottom">
                    { directionText &&
                    <p className="direction no-margin text-md text-light text-weight--normal">
                        <Icon icon="inbound" className={ directionIcon + ' text-light text-primary' } />
                        { t(directionText) + ' ' }
                    </p>
                    }
                    <ul className="list-unstyled trip-details border--bottom padding-bottom--sm">
                        <li className="text-sm text-weight--normal">
                            <span className="text-light fix-width--md">{ t('order.summary.validFrom') }</span>
                            <DateTime
                                dateTime={ journey.validFrom }
                                format="ddd DD MMM YYYY HH:mm"
                            />
                        </li>
                        <li className="text-sm text-weight--normal">
                            <span className="text-light fix-width--md">{ t('order.summary.validTo') }</span>
                            <DateTime
                                dateTime={ journey.validTo }
                                format="ddd DD MMM YYYY HH:mm"
                            />
                        </li>
                    </ul>
                </div>
            </Col>
        );
    }

    _renderJourney (directionText, journey, directionIcon, direction) {
        const { idx, orderTrip, passengerAssistRequests } = this.props;

        if (!journey.legs.size) {
            return this._renderOpenJourney(directionText, journey, directionIcon);
        }
        const departureTime = journey.getDepartureTime();
        const arrivalTime = journey.getArrivalTime();

        let matchingRequest = null;

        if (passengerAssistRequests) {
            matchingRequest = passengerAssistRequests.find(req => req.orderTripId === orderTrip.id);
        }

        const {
            outwardEnabled,
            returnEnabled
        } = matchingRequest || {};

        const isTwoSingles = !!(orderTrip.fares.outwardSingle && orderTrip.fares.returnSingle);

        let hasMatchingPAJourney = false;

        if (isTwoSingles) {
            if (idx === 0) {
                hasMatchingPAJourney = outwardEnabled;
            } else {
                hasMatchingPAJourney = returnEnabled;
            }
        } else {
            if (direction === 'Return') {
                hasMatchingPAJourney = returnEnabled;
            } else {
                hasMatchingPAJourney = outwardEnabled;
            }
        }

        return (
            <Col md={ 12 } lg={ 12 } className="margin-bottom">
                <div className="border-thick--bottom">
                    {hasMatchingPAJourney && <AccessibilityCircleIcon />}
                    <p className="direction no-margin text-md text-light text-weight--normal">
                        <Icon icon="inbound" className={ directionIcon + ' text-light text-primary' } />
                        { t(directionText) + ' ' }
                        { departureTime && <DateTime
                            className="text-primary"
                            dateTime={ departureTime }
                            format="HH:mm ddd DD MMM YYYY"
                        /> }
                    </p>

                    <ul className="list-unstyled trip-details border--bottom padding-bottom--sm ">
                        <li className="text-sm text-weight--normal">
                            <span className="text-light fix-width--sm">{ t('order.summary.dep') }</span>
                            { departureTime && <DateTime
                                dateTime={ departureTime }
                                format="HH:mm"
                            /> }
                            &nbsp;{ journey.getOrigin().name }
                        </li>

                        <li className="text-sm text-weight--normal">
                            <span className="text-light fix-width--sm">{ t('order.summary.arr') }</span>
                            { arrivalTime && <DateTime
                                dateTime={ arrivalTime }
                                format="HH:mm"
                            /> }
                            &nbsp;{ journey.getDestination().name }
                        </li>

                        <li className="text-sm text-weight--normal no-margin">
                            <span className="text-light fix-width--sm">{ t('order.summary.duration') }</span>
                            { departureTime && arrivalTime &&
                                <Duration
                                    className="text-sm"
                                    startTime={ departureTime }
                                    endTime={ arrivalTime }
                                /> }
                        </li>

                        <li className="text-sm text-weight--normal no-margin">
                            <span className="changes">
                                <span className="text-light fix-width--sm"> { t('order.summary.duration.changes', { changes: journey.getChanges() }) + ' ' }</span>
                                { journey.getChanges() > 0 ? journey.getChanges() : null }
                            </span>
                        </li>
                    </ul>

                    <ul className="list-unstyled margin-top clearfix leg__list">
                        { journey.legs.map((leg, key) => this._renderLeg(leg, key)) }
                    </ul>
                </div>
            </Col>
        );
    }

    render () {
        const { orderFare } = this.props;
        const outwardJourney = orderFare.outwardJourney;
        const returnJourney = orderFare.returnJourney;
        const outText = !orderFare.isDaySave() && 'order.summary.out';

        return (
            <div className={classnames('fare-info', { 'margin-top--sm': orderFare.isSmartcard() })}>
                <Row>
                    {this._renderJourney(outText, outwardJourney, 'otrl-rotate-180', 'Outward')}
                    {returnJourney
                        ? this._renderJourney('order.summary.return', returnJourney, null, 'Return')
                        : null}
                </Row>
            </div>
        );
    }

}


export { FareJourneys }; // export for testing

const _getPropsFromStores = function () {
    return {
        passengerAssistRequests: PassengerAssistStore.get('pendingTravelAssistRequests'),
        showSeatingPreferences: FeatureFlagStore.get('enableSeatingPreferences'),
        showPurchaseFlowReservations: FeatureFlagStore.get('enablePurchaseFlowReservations'),
    };
};

export default connectToStores(
    [ PassengerAssistStore, FeatureFlagStore ],
    _getPropsFromStores)(FareJourneys);
